import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import WizardFlow from './flow/WizardFlow';
import helper from './utils/MultiMediaWizardHelper';
import WizardData from './data/WizardData';
import Spinner from '../../../components/Spinner/Spinner';
import getLoadingText from './utils/LoadingTexts';
import UrlHelper from './utils/UrlHelper';
import Cache from './utils/Cache';
import Header from '../../../components/Header/Header';
import './multiMediaWizard.css';

const MultiMediaWizardContext = React.createContext();

/**
 * Main component for the Multi-Media Wizard.
 * Manages the wizard state, step navigation, and renders the current step.
 * @returns {JSX.Element} The wizard UI.
 */
function MultiMediaWizard() {
  const history = useHistory();
  const location = useLocation();
  /**
   * sequence id from the url params, if present indicates editing an existing sequence
   * we need to load its data
   */
  const { id: editSequenceId } = useParams();
  const [data, setData] = React.useState({
    stepIndex: 0,
    editSequenceId: editSequenceId ? Number(editSequenceId) : null,
  });
  const contextValue = React.useMemo(() => ({ data, setData }), [data, setData]);
  const wizardData = new WizardData(data, setData);
  /**
   * Get the current step based on the step index in state.
   */
  const stepIndex = data.stepIndex;
  const stepResult = WizardFlow.getStepByIndex(stepIndex);
  const Component = stepResult.component;

  /**
   * Make the wizard data available to the step component
   */
  Component.wizardData = wizardData;

  // Cleanup ------------------------------------------------------------------

  React.useEffect(() => {
    return () => {
      Cache.clearCache();
    };
  }, []);

  // Data Loading -------------------------------------------------------------

  React.useEffect(() => {
    UrlHelper.cleanupUrlParams(history, wizardData.editSequenceId);
    wizardData.navigationSetIndexes(
      WizardFlow.getPreviousStep(stepIndex).index,
      WizardFlow.getNextStep(stepIndex).index,
    );
    Component.triggerDataLoad(location.search);
  }, [stepIndex]);

  // Search Triggering --------------------------------------------------------

  React.useEffect(() => {
    Component.triggerSearchUpdate(location.search);
  }, [location.search]);

  // Render BELOW -------------------------------------------------------------

  const headerTitle = `${wizardData.sequence?.name || 'New Sequence'} - ${Component.title}`;

  return (
    <MultiMediaWizardContext.Provider value={contextValue}>
      {Component.getFilters()}
      <div className="multimedia-wizard poppins-regular">
        <Header overwrite={{ id: { name: headerTitle } }} />
        {helper.buildActionMenu(stepResult, wizardData)}
        {helper.buildBreadcrumbs(stepIndex, wizardData)}
        <Spinner
          loading={Component.dataIsLoading()}
          extraText={`${getLoadingText('loading')} (${Component.title})`}
          className="step-container"
        >
          <Component history={history} />
        </Spinner>
      </div>
    </MultiMediaWizardContext.Provider>
  );
}

export default MultiMediaWizard;
